import React from 'react';

import { ApolloError, gql, useMutation } from '@apollo/client';
import { NotificationsActive } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogTitle, Stack } from '@mui/material';
import dayjs from 'dayjs';
import { getApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { get, isNil } from 'lodash';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { APP_NAME, FCM_VAPID_KEY } from '../config';
import { i18n } from '../i18n/i18n';
import { notificationDataToRouteMap } from '../utils/libs';

export const NOTIFICATION_TOKEN_STATUS_KEY = 'NOTIFICATION_TOKEN_STATUS';
const NOTIFICATION_TOKEN_STATUS_EXPIRED_DAYS = 3;

const registerNotificationPushTokenGql = gql(`
  mutation registerNotificationPushToken($channelId: String!, $token: ID!, $tokenType: NOTIFICATION_PUSH_TOKEN_TYPE_OPTIONS!) {
    registerNotificationPushToken(channelId: $channelId, token: $token, tokenType: $tokenType) {
      success
      errors
    }
  }
`);

const unregisterNotificationPushTokenGql = gql(`
  mutation unregisterNotificationPushToken($token: ID!) {
    unregisterNotificationPushToken(token: $token) {
      success
      errors
    }
  }
`);

type NotificationUserPreference = 'accepted' | 'denied';

type LocalNotificationTokenStatus = {
  token?: string | null;
  checkAt?: string | null;
  isSurelyFirebaseServiceWorker?: boolean;
  userPreference?: NotificationUserPreference;
};

const getLocalNotiTokenStatus = () => {
  const notificationTokenStatusJson = localStorage.getItem(NOTIFICATION_TOKEN_STATUS_KEY);

  let notificationTokenStatus: LocalNotificationTokenStatus | null = null;
  if (notificationTokenStatusJson) {
    notificationTokenStatus = JSON.parse(notificationTokenStatusJson);
  }
  return notificationTokenStatus;
};

const setLocalNotiTokenStatus = (
  status: LocalNotificationTokenStatus,
  { autoUpdateCheckAtInStatus } = { autoUpdateCheckAtInStatus: true },
) => {
  const newStatus = autoUpdateCheckAtInStatus ? { ...status, checkAt: dayjs().toISOString() } : status;
  localStorage.setItem(NOTIFICATION_TOKEN_STATUS_KEY, JSON.stringify(newStatus));
};

// Resolve the user's notification preference, considering both the explicit userPreference and legacy fallback based on token and checkAt.
const getUserPreference = (localStatus: LocalNotificationTokenStatus | null): NotificationUserPreference => {
  if (localStatus?.userPreference) return localStatus.userPreference;
  // Legacy fallback: if userPreference is not set, infer from token and checkAt. (localStatus = { token: null, checkAt: <set> })
  if (!isNil(localStatus) && isNil(localStatus.token) && !isNil(localStatus.checkAt)) {
    return 'denied';
  }
  return 'accepted';
};

const notificationAvailable = () => 'serviceWorker' in navigator && 'Notification' in window;
const canAskNotificationPermission = () => notificationAvailable() && Notification.permission !== 'denied';
const hasNotificationPermission = () => notificationAvailable() && Notification.permission === 'granted';

const shouldShowNotiPermissionDialog = () => {
  if (!canAskNotificationPermission()) return false;

  const localStatus = getLocalNotiTokenStatus();
  if (localStatus?.token) return false;

  if (isNil(localStatus?.checkAt)) {
    return true;
  }
  if (dayjs(/* now */).isAfter(dayjs(localStatus?.checkAt).add(NOTIFICATION_TOKEN_STATUS_EXPIRED_DAYS, 'days'))) {
    return true;
  }

  return false;
};

const Notifications = () => {
  const serviceWorkerRegistrationRef = React.useRef<ServiceWorkerRegistration>();
  const [isServiceWorkerReady, setIsServiceWorkerReady] = React.useState(false);

  React.useEffect(() => {
    if ('serviceWorker' in navigator) {
      // Get registered PCM+PWA service worker for FCM getToken
      navigator.serviceWorker.getRegistration('/').then((serviceWorkerRegistration) => {
        if (serviceWorkerRegistration) {
          serviceWorkerRegistrationRef.current = serviceWorkerRegistration;
          setIsServiceWorkerReady(true);
        }
      });
    } else {
      Sentry.captureMessage('Browser not support service worker', {
        extra: { USER_AGENT: window.navigator.userAgent },
      });
    }
  }, []);

  const getNotificationToken = async () => {
    if (!hasNotificationPermission()) return null;

    const app = getApp();
    const messaging = getMessaging(app);
    const notificationToken = await getToken(messaging, {
      vapidKey: FCM_VAPID_KEY,
      serviceWorkerRegistration: serviceWorkerRegistrationRef.current,
    });

    return notificationToken;
  };

  const [registerNotificationPushTokenMutation] = useMutation(registerNotificationPushTokenGql);
  const registerNotificationToken = async () => {
    const notificationToken = await getNotificationToken();

    if (notificationToken) {
      try {
        const { data } = await registerNotificationPushTokenMutation({
          variables: {
            channelId: APP_NAME,
            token: notificationToken,
            tokenType: 'web',
          },
        });

        if (data?.registerNotificationPushToken.success) {
          setLocalNotiTokenStatus({
            token: notificationToken,
            isSurelyFirebaseServiceWorker: true,
            userPreference: 'accepted',
          });
        } else {
          console.warn('registerNotificationToken', data?.registerNotificationPushToken?.errors);
        }
      } catch (error) {
        if (!(error instanceof ApolloError)) throw error;
      }
    }
  };

  const [unregisterNotificationPushTokenMutation] = useMutation(unregisterNotificationPushTokenGql);
  const unregisterNotificationToken = async () => {
    const notiTokenStatus = getLocalNotiTokenStatus();

    if (notiTokenStatus?.token) {
      try {
        const { data } = await unregisterNotificationPushTokenMutation({
          variables: { token: notiTokenStatus.token },
        });

        if (data?.unregisterNotificationPushToken.success) {
          localStorage.removeItem('NOTIFICATION_TOKEN_STATUS');
        } else {
          console.warn('unregisterNotificationToken', data?.unregisterNotificationPushToken?.errors);
        }
      } catch (error) {
        if (!(error instanceof ApolloError)) throw error;
      }
    }
  };

  const upsertRegisterNotiToken = async () => {
    const storedToken = getLocalNotiTokenStatus()?.token;
    const newToken = await getNotificationToken();
    if (isNil(newToken)) return;

    if (storedToken !== newToken) {
      if (!isNil(storedToken)) {
        await unregisterNotificationToken();
      }
      await registerNotificationToken();
    }
  };

  const [showNotiPermissionDialog, setShowNotiPermissionDialog] = React.useState(false);
  const deniedNotiPermissionHandler = async () => {
    setShowNotiPermissionDialog(false);
    await unregisterNotificationToken();
    setLocalNotiTokenStatus({ token: null, userPreference: 'denied' });
  };

  React.useEffect(() => {
    if (!isServiceWorkerReady) return;

    setShowNotiPermissionDialog(shouldShowNotiPermissionDialog());
  }, [isServiceWorkerReady]);

  // FCM issues a fresh token whenever the device permission is toggled off
  // and back on; reconcile the new one with the backend on every SW-ready tick.
  React.useEffect(() => {
    if (!isServiceWorkerReady) return;

    const localStatus = getLocalNotiTokenStatus();
    if (getUserPreference(localStatus) === 'denied') return;

    upsertRegisterNotiToken();
  }, [isServiceWorkerReady]);

  const navigate = useNavigate();

  // Handle foreground notification
  React.useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    const app = getApp();
    const messaging = getMessaging(app);
    const unsubscribe = onMessage(messaging, async (payload) => {
      // Foreground Message goes here
      if (payload.notification?.title) {
        const notificationDataJson = get(payload, ['data', 'data'], null);
        let notificationData: { [key: string]: unknown } | null = null;
        try {
          notificationData = JSON.parse(notificationDataJson);
        } catch (error) {
          notificationData = null;
        }
        const { routePath, isExternal, params } = notificationDataToRouteMap(notificationData);

        // Use service worker registration to show notification
        const registration =
          serviceWorkerRegistrationRef.current || (await navigator.serviceWorker.getRegistration('/'));
        if (registration) {
          await registration.showNotification(payload.notification.title, {
            body: payload.notification.body,
            data: payload.data,
          });
        }
        // onClick foreground notifications
        // is already handled by ~/public/findtemp-notification-handler.js
        //   self.addEventListener('notificationclick', (event) => {...});
      }
    });

    // eslint-disable-next-line consistent-return
    return unsubscribe;
  }, [navigate]);

  const requestNotificationPermissionHandler = async () => {
    setShowNotiPermissionDialog(false);

    const result = await Notification.requestPermission();
    if (result === 'granted') {
      upsertRegisterNotiToken();
    }
  };

  return (
    <Dialog open={showNotiPermissionDialog} onClose={deniedNotiPermissionHandler}>
      <DialogTitle>
        <Stack flexDirection="row" alignItems="center" gap={2}>
          <NotificationsActive />
          {i18n.t('components.notifications.notification_permission_request')}
        </Stack>
      </DialogTitle>

      <DialogActions>
        <Button onClick={deniedNotiPermissionHandler} data-testid="get-notification-popup-close-button">
          {i18n.t('general.denied')}
        </Button>
        <Button onClick={requestNotificationPermissionHandler}>{i18n.t('general.accept')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default Notifications;
